// src/components/ai/AiResultCard.js
import React from 'react';
import AiAttributionBadge from './AiAttributionBadge';

/**
 * Generic display shell for any AI-generated result.
 *
 * Props:
 *   title        — section heading string
 *   loading      — bool; renders skeleton instead of content
 *   error        — string | null; renders error state
 *   result       — object with { text, basedOn, confidence } or null
 *   children     — optional override for the content area
 *   actions      — optional node rendered below attribution (e.g. copy button)
 *   className    — extra Tailwind classes on the outer wrapper
 */
function AiResultCard({ title, loading = false, error = null, result = null, children, actions, className = '' }) {
  const hasResult = result && result.text;

  return (
    <div className={`rounded-2xl border border-violet-100 bg-violet-50 p-4 ${className}`.trim()}>
      {title ? (
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-violet-500">{title}</p>
      ) : null}

      {loading ? (
        <div className="mt-3 space-y-2" aria-label="Loading AI result">
          <div className="h-3 w-3/4 animate-pulse rounded bg-violet-200" />
          <div className="h-3 w-full animate-pulse rounded bg-violet-200" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-violet-200" />
        </div>
      ) : error ? (
        <p className="mt-3 text-sm text-rose-600">{error}</p>
      ) : hasResult ? (
        <div className="mt-3">
          {children || (
            <p className="text-sm text-slate-700">{result.text}</p>
          )}
          <AiAttributionBadge
            basedOn={result.basedOn || []}
            confidence={result.confidence || 'low'}
          />
          {actions ? <div className="mt-3">{actions}</div> : null}
        </div>
      ) : (
        <p className="mt-3 text-sm text-slate-400">No AI result yet.</p>
      )}
    </div>
  );
}

export default AiResultCard;
